import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faUser, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "../../../../i18n/LanguageContext";
import UserForm from "./UserForm";

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Up to this many seats we show the friendly per-seat avatar grid (family
// plans). Above it (e.g. SMB 25/50/100) the grid becomes an unwieldy wall of
// dots, so we switch to a compact capacity meter instead.
const SEAT_AVATAR_THRESHOLD = 8;

/**
 * AddMemberSheet
 * Slide-in side sheet for creating a new member user.
 * Replaces the old "Create User" accordion section.
 */
function AddMemberSheet({
  isOpen,
  onClose,
  subscriptionInfo,
  hasActiveSubscription,
  formData,
  errors,
  loading,
  availableGroups,
  groupsLoading,
  groupsError,
  onInputChange,
  onSubmit,
  onRefreshGroups,
  onNavigateBilling,
}) {
  const { t } = useTranslation();
  const sheetRef = useRef(null);
  const prevFocusRef = useRef(null);

  // On open: capture current focus & move into sheet; on close: restore
  useEffect(() => {
    if (isOpen) {
      prevFocusRef.current = document.activeElement;
      requestAnimationFrame(() => {
        const first = sheetRef.current?.querySelector(FOCUSABLE);
        first?.focus();
      });
    } else {
      prevFocusRef.current?.focus();
    }
  }, [isOpen]);

  // Trap focus inside sheet, close on Escape
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key !== "Tab") return;
    const nodes = Array.from(
      sheetRef.current?.querySelectorAll(FOCUSABLE) ?? [],
    );
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  // Lock body scroll while open so the page behind cannot scroll into view
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const isAtLimit = subscriptionInfo?.isAtLimit;
  const isNearLimit = subscriptionInfo?.isNearLimit;

  const sheet = (
    <div className="um-sheet-root">
      {/* Backdrop */}
      <div className="um-sheet-overlay" onClick={onClose} aria-hidden="true" />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className="um-sheet-panel"
        role="dialog"
        aria-modal="true"
        aria-label={t("usermgmt.sheet.addMemberTitle")}
        onKeyDown={handleKeyDown}
      >
        <div className="um-sheet-header">
          <div className="um-sheet-header-left">
            <span className="um-sheet-header-icon">
              <FontAwesomeIcon icon={faUserPlus} />
            </span>
            <h2 className="um-sheet-title">
              {t("usermgmt.sheet.addMemberTitle")}
            </h2>
          </div>
          <button
            className="um-sheet-close"
            onClick={onClose}
            aria-label={t("common.close")}
            type="button"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="um-sheet-body">
          {/* Subscription seat widget */}
          {subscriptionInfo &&
            !subscriptionInfo.loading &&
            subscriptionInfo.maxUsers != null &&
            (subscriptionInfo.maxUsers <= SEAT_AVATAR_THRESHOLD ? (
              /* Small accounts (family plans): friendly per-seat avatar grid */
              <div className="um-sheet-slots">
                <div className="user-icons-row">
                  {Array.from({ length: subscriptionInfo.maxUsers }).map(
                    (_, i) => (
                      <span
                        key={i}
                        className={`user-icon-slot ${i < subscriptionInfo.currentUsers ? "filled" : "empty"}${
                          isAtLimit
                            ? " at-limit"
                            : isNearLimit
                              ? " near-limit"
                              : ""
                        }`}
                        title={
                          i < subscriptionInfo.currentUsers
                            ? `User ${i + 1} (active)`
                            : `Slot ${i + 1} (available)`
                        }
                      >
                        <FontAwesomeIcon icon={faUser} />
                      </span>
                    ),
                  )}
                </div>
                <p className="um-sheet-slots-label">
                  {subscriptionInfo.currentUsers} of {subscriptionInfo.maxUsers}{" "}
                  {t("usermgmt.sheet.slotsUsed")}
                </p>
              </div>
            ) : (
              /* Large accounts (SMB/business): compact capacity meter */
              <div
                className={`um-sheet-seats${
                  isAtLimit ? " at-limit" : isNearLimit ? " near-limit" : ""
                }`}
              >
                <div className="um-sheet-seats-top">
                  <span className="um-sheet-seats-count">
                    <FontAwesomeIcon icon={faUser} aria-hidden="true" />
                    <span>
                      <strong>{subscriptionInfo.currentUsers}</strong> /{" "}
                      {subscriptionInfo.maxUsers}{" "}
                      {t("usermgmt.sheet.seatsUsed")}
                    </span>
                  </span>
                  <span className="um-sheet-seats-remaining">
                    {Math.max(
                      subscriptionInfo.maxUsers - subscriptionInfo.currentUsers,
                      0,
                    )}{" "}
                    {t("usermgmt.sheet.seatsAvailable")}
                  </span>
                </div>
                <div
                  className="um-sheet-seats-meter"
                  role="progressbar"
                  aria-valuenow={subscriptionInfo.currentUsers}
                  aria-valuemin={0}
                  aria-valuemax={subscriptionInfo.maxUsers}
                  aria-label={`${subscriptionInfo.currentUsers} of ${subscriptionInfo.maxUsers} ${t(
                    "usermgmt.sheet.seatsUsed",
                  )}`}
                >
                  <div
                    className="um-sheet-seats-meter-fill"
                    style={{
                      width: `${Math.min(
                        (subscriptionInfo.currentUsers /
                          subscriptionInfo.maxUsers) *
                          100,
                        100,
                      )}%`,
                    }}
                  />
                </div>
              </div>
            ))}

          {/* Limit / no-subscription warnings */}
          {!hasActiveSubscription && (
            <div className="common-warning-box critical">
              <p>
                🚫 <strong>{t("usermgmt.create.noSubscription")}</strong>{" "}
                {t("usermgmt.create.noSubscriptionMsg")}
              </p>
              <button
                type="button"
                className="upgrade-subscription-btn"
                onClick={onNavigateBilling}
              >
                {t("usermgmt.create.activateSubscription")}
              </button>
            </div>
          )}

          {hasActiveSubscription && isAtLimit && (
            <div className="common-warning-box at-limit">
              <p>
                🚫 <strong>{t("usermgmt.create.atLimit")}</strong>{" "}
                {t("usermgmt.create.atLimitMsg").replace(
                  "{maxUsers}",
                  subscriptionInfo.maxUsers,
                )}
              </p>
              {!subscriptionInfo.isHighestTier && (
                <button
                  type="button"
                  className="upgrade-subscription-btn"
                  onClick={onNavigateBilling}
                >
                  {t("usermgmt.create.upgradeSubscription")}
                </button>
              )}
            </div>
          )}

          {hasActiveSubscription && isNearLimit && !isAtLimit && (
            <div className="common-warning-box near-limit">
              <p>
                ⚠️ <strong>{t("usermgmt.create.nearLimit")}</strong>{" "}
                {t("usermgmt.create.nearLimitMsg")
                  .replace(
                    "{remaining}",
                    subscriptionInfo.maxUsers - subscriptionInfo.currentUsers,
                  )
                  .replace("{maxUsers}", subscriptionInfo.maxUsers)}
              </p>
            </div>
          )}

          {/* Form */}
          <div
            className={`form-container ${isAtLimit || !hasActiveSubscription ? "disabled" : ""}`}
          >
            <UserForm
              mode="create"
              formData={formData}
              errors={errors}
              loading={loading}
              disabled={isAtLimit || !hasActiveSubscription}
              onInputChange={onInputChange}
              onSubmit={onSubmit}
              availableGroups={availableGroups}
              groupsLoading={groupsLoading}
              groupsError={groupsError}
              onRefreshGroups={onRefreshGroups}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}

export default AddMemberSheet;
